import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '../api/client';

const FunnelStep = ({ label, value, percent, color, isLast }) => (
  <div className="relative flex items-center mb-6 last:mb-0">
    <div className={`w-32 text-sm font-medium ${isLast ? 'text-emerald-400' : 'text-slate-300'}`}>
      {label}
    </div>
    <div className="flex-1 h-12 bg-white/5 rounded-lg relative overflow-hidden mx-4">
      <motion.div 
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="h-full rounded-lg relative"
        style={{ backgroundColor: color, opacity: 0.8 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10" />
      </motion.div>
      <div className="absolute inset-0 flex items-center pl-4 text-white font-bold z-10">
        {value}
      </div>
    </div>
    <div className="w-16 text-right text-sm text-slate-500 font-mono">
      {percent}%
    </div>
  </div>
);

export default function Reports() {
  const [funnelData, setFunnelData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const res = await api.get('/reports/funnel');
      setFunnelData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Aggregate total funnel
  const totalClicks = funnelData.reduce((sum, item) => sum + parseInt(item.total_clicks), 0);
  const totalJoined = funnelData.reduce((sum, item) => sum + parseInt(item.joined), 0);
  const totalCustomers = funnelData.reduce((sum, item) => sum + parseInt(item.customers), 0);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Сквозная аналитика</h1>
        <p className="text-slate-400">Воронка конверсии от первого клика до покупки</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Main Funnel Visualization */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface rounded-xl p-6 border border-white/5"
        >
          <h3 className="text-lg font-bold text-white mb-6">Общая воронка</h3>
          
          <div className="space-y-2">
            <FunnelStep 
              label="Переходы" 
              value={totalClicks} 
              percent={100} 
              color="#3b82f6" 
            />
            <FunnelStep 
              label="Подписки" 
              value={totalJoined} 
              percent={totalClicks > 0 ? ((totalJoined / totalClicks) * 100).toFixed(1) : 0} 
              color="#8b5cf6" 
            />
            <FunnelStep 
              label="Покупки" 
              value={totalCustomers} 
              percent={totalClicks > 0 ? ((totalCustomers / totalClicks) * 100).toFixed(1) : 0} 
              color="#10b981" 
              isLast
            />
          </div>
        </motion.div>

        {/* ROI / Revenue by Source */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-surface rounded-xl p-6 border border-white/5 flex flex-col"
        >
          <h3 className="text-lg font-bold text-white mb-6">Выручка по источникам</h3>
          
          {funnelData.length > 0 ? (
            <div className="flex-1 min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="source" stroke="#64748b" tick={{fill: '#64748b'}} />
                  <YAxis stroke="#64748b" tick={{fill: '#64748b'}} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="revenue" name="Выручка (₽)" radius={[4, 4, 0, 0]}>
                    {funnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'][index % 4]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
             <div className="flex-1 flex items-center justify-center text-slate-500 border border-white/5 border-dashed rounded-lg">
               Нет данных о продажах
             </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
